import { GiftVoucherStatus } from "../../../../common/enums";
import { GiftVoucherStatusLabels } from "../../../../common/labels";
import { IGiftVoucherModel } from "../models/giftVoucherModels";

/**
 * The app is EUR-only (see docs/currency.md); vouchers are always issued and spent in
 * euro, so the symbol is fixed rather than resolved per row.
 */
export const formatVoucherAmount = (amount: number) =>
  `${amount.toFixed(2)} EUR`;

/**
 * What to show as the voucher's state.
 *
 * `isExpired` wins over the stored status on purpose: the nightly breakage sweep may not
 * have run yet, and a voucher whose last day is over must read as expired the moment it
 * stops working, not whenever a job next gets around to it.
 */
export const getVoucherStatusLabel = (voucher: IGiftVoucherModel) => {
  if (voucher.isExpired && voucher.status !== GiftVoucherStatus.Redeemed) {
    return GiftVoucherStatusLabels[GiftVoucherStatus.Expired];
  }

  return GiftVoucherStatusLabels[voucher.status];
};

/** SCSS modifier suffix for the status badge, matching `.badge_<key>` in the styles. */
export const getVoucherStatusKey = (voucher: IGiftVoucherModel) => {
  if (voucher.isExpired && voucher.status !== GiftVoucherStatus.Redeemed) {
    return "expired";
  }

  switch (voucher.status) {
    case GiftVoucherStatus.New:
      return "new";
    case GiftVoucherStatus.Sold:
      return "sold";
    case GiftVoucherStatus.Redeemed:
      return "redeemed";
    default:
      return "expired";
  }
};

/**
 * Groups the 10-character code in blocks of 4 ("ABCD-EFGH-IJ") so it can be read out
 * loud and typed back without losing the place. The raw code is what gets copied.
 */
export const formatVoucherCode = (code: string) =>
  (code.match(/.{1,4}/g) ?? [code]).join("-");

/**
 * The two amounts a voucher row shows. An UNSOLD voucher has no balance yet (nothing has
 * been paid for it), so showing "0.00 от 100.00" reads as if it were worthless — its face
 * value leads instead. Once sold, what it is worth RIGHT NOW leads, with the face value as
 * context only when something has been spent off it.
 */
export const getVoucherDisplayAmounts = (voucher: IGiftVoucherModel) => {
  if (voucher.status === GiftVoucherStatus.New) {
    return {
      primary: formatVoucherAmount(voucher.initialAmount),
      secondary: "номинал · не е продаден",
    };
  }

  return {
    primary: formatVoucherAmount(voucher.remainingAmount),
    secondary:
      voucher.remainingAmount !== voucher.initialAmount
        ? `остатък от ${formatVoucherAmount(voucher.initialAmount)}`
        : "пълна стойност",
  };
};

/** Who holds the voucher, with the client's ID so staff can find the exact profile. */
export const formatVoucherHolder = (voucher: IGiftVoucherModel) =>
  voucher.holderUserId && voucher.holderUserName
    ? `${voucher.holderUserName} (#${voucher.holderUserId})`
    : "Не е свързан с клиент";
